import { sendsSessionCookie } from './session-key-kind.js'

// Cookie mode (core-backend docs/auth_refactor.md §12): the session JWT sits in an httpOnly cookie on the backend's
// host, which a browser only sends on a request made with credentials. The app reaches the backend through
// superagent (XMLHttpRequest), RTK Query and bare fetch, so the credentials are added here, once, for the backend's
// origin only, rather than at every call site. Outside cookie mode nothing changes.
//
// Responses the browser cached before cookie mode say "Access-Control-Allow-Origin: *", which a request with
// credentials may not use, and the browser cache does not tell the two kinds of request apart (api/publicholiday
// keeps 30 days, a few lookups up to 12 hours). So the first GET of each URL in a page load goes to the server; what
// the browser caches then also fits requests with credentials, and later GETs of that URL use the cache as before.
//
// Temporary: every answer the backend gives the listed origins now allows credentials, so only copies cached before
// that backend deploy are in the way. Remove revalidatesCache 30 days after it (core-backend docs/auth_refactor.md
// §12, "Cache skip is temporary").

let installed = false

export const installSessionCookieTransport = (backendUrl) => {
    if (installed || typeof window === 'undefined') return
    installed = true

    const revalidated = new Set()
    const revalidatesCache = (method, url) => {
        if (String(method).toUpperCase() !== 'GET' || revalidated.has(url)) return false
        revalidated.add(url)
        return true
    }

    const open = XMLHttpRequest.prototype.open
    XMLHttpRequest.prototype.open = function openWithSessionCookie(method, url, ...rest) {
        open.call(this, method, url, ...rest)
        if (!sendsSessionCookie(String(url), backendUrl)) return
        try {
            this.withCredentials = true
        } catch {
            // A synchronous request cannot take credentials; it goes as before and the backend answers 401.
            return
        }
        if (revalidatesCache(method, String(url))) this.setRequestHeader('Cache-Control', 'no-cache')
    }

    const nativeFetch = window.fetch.bind(window)
    window.fetch = (input, init) => {
        const url = typeof input === 'string' || input instanceof URL ? String(input) : input?.url
        if (!sendsSessionCookie(url, backendUrl)) return nativeFetch(input, init)

        // A caller that says omit (answersWithoutCookie) goes without the cookie.
        if (init?.credentials === 'omit') return nativeFetch(input, init)

        const method = init?.method ?? input?.method ?? 'GET'
        // A caller that picked a cache mode keeps it.
        const callerCacheMode = (init?.cache ?? input?.cache ?? 'default') !== 'default'
        const cache = !callerCacheMode && revalidatesCache(method, url) ? { cache: 'no-cache' } : {}
        return nativeFetch(input, { ...init, credentials: 'include', ...cache })
    }
}

/**
 * Whether the backend answers this URL at all when asked without the cookie. A backend that stopped taking the cookie
 * from this origin (rolled back to before cookie mode, or the origin taken out of SignInV2:Jwt:Cookie:Origins) still
 * answers such a call, while the browser blocks every answer to one with credentials; offline, neither gets through.
 */
export const answersWithoutCookie = async (url) => {
    try {
        await window.fetch(url, { credentials: 'omit', cache: 'no-store' })
        return true
    } catch {
        return false
    }
}
